import React, { JSX, useEffect, useRef, useState } from 'react';
import { Field, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Icons from 'components/atoms/Icons';
import { formatProviderUrl } from 'assets/utils/formatUrl';
import Axios from 'axios';
import { getProvidersSearchSrc, getSearchWidget } from 'lib/util';
import { ProviderFields } from 'components/types/MapTypes';
import { ItemValue } from 'components/types/ListItem';
import { Specialty } from 'components/types/SpecialtyTypes';
import Modal from 'assets/shared/Modal';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import { StyleguideComponentProps } from 'lib/component-props';
import { ProviderItem } from 'components/types/ProviderTypes';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { classNames } from 'assets/utils/helper';
import { LocationItem } from 'components/types/LocationTypes';
import Constants from '../../assets/utils/constant';

const AZURE_MAPS_ENDPOINT = 'https://atlas.microsoft.com';

const getAzureMapsKey = () =>
  process.env.NEXT_PUBLIC_AZURE_MAPS_SUBSCRIPTION_KEY;

type LatLng = {
  lat: number;
  lng: number;
};

type AzureSuggestion = {
  id: string;
  label: string;
  address: {
    label?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

const buildContentHubProviderImageUrl = (
  firstName?: string,
  lastName?: string,
  npi?: string
): string => {
  if (!firstName || !lastName || !npi) {
    return '';
  }

  const cleanValue = (value: string): string =>
    value
      .trim()
      .toLowerCase()
      .replace(/\.\s+/g, '.')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9.-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  return `${Constants.api.contentHubProviderImageBaseUrl}/${cleanValue(
    firstName
  )}-${cleanValue(lastName)}-${npi}.jpg`;
};

const getProviderFallbackImage = (gender?: string): string =>
  `${Constants.api.contentHubProviderImageBaseUrl}/placeholder-${
    gender === 'Male' ? 'male' : 'female'
  }-provider-transparent`;

type DynamicProviderContentProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    Title: Field<string>;
    Description: Field<string>;
    Providers: Array<ProviderFields>;
    EmploymentStatus: Array<ItemValue>;
    Keywords: Field<string>;
    Specialties: Array<Specialty>;
    ZipCode: Field<string>;
    Radius: ItemValue;
  };
};

let defaultSearchBody: any = {
  content: {},
  limit: 100,
};

type ProviderLocation = {
  id: string;
  city: string;
};

function getUniqueCities(locations: Array<ProviderLocation>): string[] {
  return [...new Set(locations.map(loc => loc.city))];
}

type ProviderCitiesProps = {
  locations: Array<ProviderLocation>;
};

const ProviderCities = ({ locations }: ProviderCitiesProps) => {
  const cities = getUniqueCities(locations);

  if (cities.length === 0) return null;

  return (
    <div className="cities">
      {cities.map((city, idx) => (
        <React.Fragment key={idx}>
          {city}
          {idx < cities.length - 1 && ', '}
        </React.Fragment>
      ))}
    </div>
  );
};

type ProviderCardProps = {
  provider: ProviderFields;
};

const ProviderCard = (props: ProviderCardProps) => {
  const { provider } = props;

  const providerImageUrl = buildContentHubProviderImageUrl(
    provider.firstname,
    provider.lastname,
    provider.npi
  );

  const fallbackImage = getProviderFallbackImage(provider.gender);

  return (
    <div className="provider-item bg-secondary">
      <a
        href={formatProviderUrl(
          provider.externalid,
          provider.firstname,
          provider.lastname
        )}
        className="featured-item"
      >
        <div className="card-wrapper">
          <div
            className="item-image"
            dangerouslySetInnerHTML={{
              __html: `<img 
                src="${providerImageUrl || fallbackImage}" 
                alt="${`image-${provider.gender}`}"
                onerror="this.onerror=null;this.src='${fallbackImage}';"
              />`,
            }}
          ></div>

          <div className="content-wrapper">
            <div>
              {(provider?.fullname || provider?.degrees) && (
                <Text
                  className="item-name osf-display-h3"
                  itemProp="name"
                  tag="h3"
                  field={{
                    value: `${provider?.fullname || ''}, ${
                      provider?.degrees || ''
                    }`,
                  }}
                />
              )}
            </div>

            <div className="location">
              <div className="address">
                <div className="icon icon-location">
                  <Icons id="icon-pdc-location" />
                </div>
                <ProviderCities locations={provider.locations} />
              </div>
            </div>
          </div>

          <div className="bottom-section">
            <div className="divider"></div>
            <div className="specialty">
              {provider.specialty?.length > 0 && (
                <p
                  className="item-subtitle"
                  itemType="https://schema.org/medicalSpecialty"
                >
                  {provider.specialty[0]}
                </p>
              )}
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};

function getManualCities(locations: Array<LocationItem>): string[] {
  if (!locations) return [];
  return [...new Set(locations.map(loc => loc.fields.City.value))];
}

type ManualProviderCitiesProps = {
  locations: Array<LocationItem>;
};

const ManualProviderCities = ({ locations }: ManualProviderCitiesProps) => {
  const cities = getManualCities(locations);

  if (cities.length === 0) return null;

  return <div className="cities">{cities.join(', ')}</div>;
};

type ManualProviderCardProps = {
  provider: ProviderItem;
};

const ManualProviderCard = (props: ManualProviderCardProps) => {
  const { provider } = props;

  const providerImageUrl = buildContentHubProviderImageUrl(
    provider.fields.FirstName.value,
    provider.fields.LastName.value,
    provider.fields.NPI?.value
  );

  const fallbackImage = getProviderFallbackImage(provider.fields.Gender.value);

  return (
    <div className="provider-item bg-secondary">
      <a
        href={formatProviderUrl(
          provider.fields.ExternalId.value,
          provider.fields.FirstName.value,
          provider.fields.LastName.value
        )}
        className="featured-item"
      >
        <div className="card-wrapper">
          <div
            className="item-image"
            dangerouslySetInnerHTML={{
              __html: `<img 
                src="${providerImageUrl || fallbackImage}" 
                alt="${`image-${provider.fields.Gender.value}`}"
                onerror="this.onerror=null;this.src='${fallbackImage}';"
              />`,
            }}
          ></div>

          <div className="content-wrapper">
            {(provider?.fields.FullName.value ||
              provider?.fields.Degrees.value) && (
              <Text
                className="item-name osf-display-h3"
                itemProp="name"
                tag="h3"
                field={{
                  value: `${provider?.fields.FullName.value || ''}, ${
                    provider?.fields.Degrees.value || ''
                  }`,
                }}
              />
            )}

            <div className="location">
              <div className="address">
                <div className="icon icon-location">
                  <Icons id="icon-pdc-location" />
                </div>
                <ManualProviderCities
                  locations={provider.fields.Locations.value}
                />
              </div>
            </div>
          </div>

          <div className="bottom-section">
            <div className="divider"></div>
            <div className="specialty">
              {provider.fields.Specialty?.value.length > 0 && (
                <p
                  className="item-subtitle"
                  itemType="https://schema.org/medicalSpecialty"
                >
                  {provider.fields.Specialty.value.split('|')[0]}
                </p>
              )}
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};

type BreakpointsCountType = {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
};

const buildLocationString = (address: AzureSuggestion['address']) => {
  return (
    address.label ||
    `${address.city ? `${address.city}, ` : ''}${
      address.state ? `${address.state} ` : ''
    }${address.zip ?? ''}`.trim()
  );
};

const isIllinoisSuggestion = (suggestion: AzureSuggestion): boolean => {
  const state = suggestion.address.state?.trim().toLowerCase() || '';
  const label = suggestion.label?.trim().toLowerCase() || '';

  return (
    state === 'il' ||
    state === 'illinois' ||
    label.includes(', il') ||
    label.includes(', illinois') ||
    label.endsWith(' il') ||
    label.endsWith(' illinois')
  );
};

const getProviderIdentityKey = (
  provider: ProviderFields | ProviderItem
): string => {
  if ('fields' in provider) {
    return (
      provider.id ||
      provider.fields.ExternalId?.value ||
      provider.fields.NPI?.value ||
      provider.fields.FullName?.value ||
      ''
    );
  }

  return (
    provider.id ||
    // provider.source_id ||
    provider.externalid ||
    provider.npi ||
    provider.fullname ||
    ''
  );
};

const DynamicProviderContent = (
  props: DynamicProviderContentProps
): JSX.Element => {
  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const [isDynamic, setIsDynamic] = useState(false);
  const [providers, setProviders] = useState(
    Array<ProviderFields | ProviderItem>
  );
  const [totalProvidersCount, setTotalProvidersCount] = useState(0);
  const [size, setSize] = useState('');
  const [providersShowingCount, setProvidersShowingCount] = useState(0);
  const [providersShowing, setProvidersShowing] = useState(
    Array<ProviderFields | ProviderItem>
  );
  const [inputLocation, setInputLocation] = useState('');
  const [coords, setCoords] = useState<LatLng | null>();
  const [location, setLocation] = useState('');
  const [geolocating, setGeolocating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(true);
  const [suggestions, setSuggestions] = useState<Array<AzureSuggestion>>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const suggestionRef = useRef<AzureSuggestion | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const DEFAULT_DISTANCE = 80.47;

  const breakpointResultsCount: BreakpointsCountType = {
    xs: 3,
    sm: 3,
    md: 6,
    lg: 6,
    xl: 6,
  };

  const breakpointRowsCount: BreakpointsCountType = {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 2,
    xl: 3,
  };

  const convertToKM = (miles: number) => {
    return miles / 0.621371192;
  };

  const convertToMI = (km: number) => {
    return km * 0.621371192;
  };

  const setAddressState = (
    address: AzureSuggestion['address'],
    updateInput = true
  ) => {
    const locationString = buildLocationString(address);

    setLocation(locationString);

    if (updateInput) {
      setInputLocation(locationString);
    }
  };

  const fetchAzureSuggestions = async (query: string) => {
    const subscriptionKey = getAzureMapsKey();
    const trimmedQuery = query.trim();

    if (!subscriptionKey || !trimmedQuery || trimmedQuery.length <= 1) {
      setSuggestions([]);
      suggestionRef.current = null;
      return;
    }

    try {
      setIsLoadingSuggestions(true);

      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/fuzzy/json`,
        {
          params: {
            'api-version': '1.0',
            query: trimmedQuery,
            countrySet: 'US',
            limit: 15,
            typeahead: true,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      const normalizedSuggestions = results
        .map((result: any) => {
          const address = result.address;

          const city = address?.municipality;
          const state = address?.countrySubdivision;
          const zip = address?.postalCode;

          const label =
            address?.freeformAddress ||
            result.poi?.name ||
            [city, state, zip].filter(Boolean).join(', ');

          return {
            id: `${label}-${result.type ?? ''}`,
            label,
            address: {
              label,
              city,
              state,
              zip,
            },
          };
        })
        .filter((suggestion: AzureSuggestion) => suggestion.label);

      const uniqueSuggestions = normalizedSuggestions.filter(
        (suggestion: AzureSuggestion, index: number, self: AzureSuggestion[]) =>
          index === self.findIndex(item => item.label === suggestion.label)
      );

      const illinoisSuggestions =
        uniqueSuggestions.filter(isIllinoisSuggestion);

      const otherSuggestions = uniqueSuggestions.filter(
        (suggestion: AzureSuggestion) => !isIllinoisSuggestion(suggestion)
      );

      const prioritizedSuggestions = [
        ...illinoisSuggestions,
        ...otherSuggestions,
      ];

      setSuggestions(prioritizedSuggestions.slice(0, 5));
      suggestionRef.current = prioritizedSuggestions[0] || null;
    } catch (error: any) {
      console.error('Azure fuzzy search error:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      setSuggestions([]);
      suggestionRef.current = null;
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const geocodeSelectedSuggestion = async (suggestion: AzureSuggestion) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) return;

    try {
      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/search/address/json`,
        {
          params: {
            'api-version': '1.0',
            query: suggestion.label,
            countrySet: 'US',
            limit: 1,
            'subscription-key': subscriptionKey,
          },
          headers: {
            'Accept-Language': 'en-US',
          },
        }
      );

      const result = response.data?.results?.[0];

      if (!result?.position?.lat || !result?.position?.lon) return;

      const latLng: LatLng = {
        lat: result.position.lat,
        lng: result.position.lon,
      };

      const address = {
        label: suggestion.label,
        city: suggestion.address.city || result?.address?.municipality,
        state: suggestion.address.state || result?.address?.countrySubdivision,
        zip: suggestion.address.zip || result?.address?.postalCode,
      };

      setProviders([]);
      setCoords(latLng);
      setAddressState(address);
      setSuggestions([]);
    } catch {
      setSuggestions([]);
    }
  };

  const reverseGeocode = async (latLng: LatLng, userLoc?: boolean) => {
    const subscriptionKey = getAzureMapsKey();

    if (!subscriptionKey) {
      setGeolocating(false);
      return;
    }

    try {
      const response = await Axios.get(
        `${AZURE_MAPS_ENDPOINT}/reverseGeocode`,
        {
          params: {
            'api-version': '2026-01-01',
            coordinates: `${latLng.lng},${latLng.lat}`,
            'subscription-key': subscriptionKey,
            language: 'en-US',
          },
        }
      );

      const azureAddress = response.data?.features?.[0]?.properties?.address;

      const address = {
        city: azureAddress?.locality || azureAddress?.municipality,
        state:
          azureAddress?.adminDistricts?.[0]?.shortName ||
          azureAddress?.adminDistricts?.[0]?.name,
        zip: azureAddress?.postalCode,
      };

      setGeolocating(false);
      setAddressState(address, !userLoc);
    } catch {
      setGeolocating(false);
    }
  };

  const handleChange = (address: string) => {
    setInputLocation(address);
  };

  const handleSelect = async (suggestion?: AzureSuggestion) => {
    const selectedSuggestion = suggestion || suggestionRef.current;

    setProviders([]);

    const outerModal = document.querySelector('.pdc-modal') as HTMLElement;
    outerModal && outerModal.click();

    if (!selectedSuggestion) return;

    await geocodeSelectedSuggestion(selectedSuggestion);
  };

  const geolocate = () => {
    setGeolocating(true);
    setInputLocation('');
    setProviders([]);

    navigator.geolocation.getCurrentPosition(
      position => {
        if (
          position &&
          position.coords &&
          position.coords.latitude &&
          position.coords.longitude
        ) {
          const latLng: LatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setCoords(latLng);
          reverseGeocode(latLng, true);
        }
      },
      () => {
        setNoResults(true);
        setCoords(null);
        setLocation('');
        setInputLocation('');
        setGeolocating(false);
        showSelected();
      }
    );
  };

  const search = async () => {
    setSearching(true);

    let geoContext = {};
    const filters = [];

    defaultSearchBody = {
      content: {},
      limit: 100,
    };

    if (coords) {
      geoContext = {
        context: {
          geo: {
            location: {
              lat: coords.lat,
              lon: coords.lng,
            },
          },
        },
      };

      filters.push({
        type: 'geoDistance',
        name: 'locationscoordinates',
        distance: props.fields.Radius?.fields?.heading?.value
          ? `${convertToKM(
              parseInt(props.fields.Radius.fields.heading.value)
            )}km`
          : `${DEFAULT_DISTANCE}km`,
        lat: coords.lat,
        lon: coords.lng,
      });

      defaultSearchBody = {
        ...defaultSearchBody,
        sort: {
          value: [
            {
              name: 'coordinates',
              order: 'asc',
            },
          ],
        },
      };
    }

    if (props.fields.Specialties?.length > 0) {
      const specialtiesFilters = props.fields.Specialties.map(specialty => ({
        type: 'eq',
        name: 'specialtyfilters',
        value: specialty.name.replaceAll('-', ' '),
      }));

      filters.push({ type: 'or', filters: [...specialtiesFilters] });
    }

    if (props.fields.Keywords?.value?.length > 0) {
      defaultSearchBody.query = {
        keyphrase: props.fields.Keywords.value,
      };
    }

    if (props.fields.EmploymentStatus?.length > 0) {
      const statusFilters = props.fields.EmploymentStatus.filter(
        status => status?.fields?.heading?.value
      ).map(status => ({
        type: 'eq',
        name: 'sortfield',
        value: status.fields.heading.value,
      }));

      if (statusFilters.length > 0) {
        filters.push({ type: 'or', filters: statusFilters });
      }
    }

    if (props.fields.Keywords?.value?.length > 0) {
      defaultSearchBody.query = {
        keyphrase: props.fields.Keywords.value,
      };
    }

    defaultSearchBody.filter = {
      type: 'and',
      filters,
    };

    try {
      const searchReq = {
        ...geoContext,
        widget: {
          items: [
            {
              rfk_id: getSearchWidget(),
              entity: 'provider',
              sources: [getProvidersSearchSrc()],
              search: defaultSearchBody,
            },
          ],
        },
      };
      const response = await Axios.post('/api/discovery', searchReq);

      const data = response.data;

      if (data.widgets[0].total_item > 0) {
        setNoResults(false);

        const providers = [...data.widgets[0].content] as Array<ProviderFields>;
        const totalItems = data.widgets[0]?.total_item || providers.length;
        const totalPages = Math.ceil(totalItems / defaultSearchBody.limit);

        for (let pageIndex = 1; pageIndex < totalPages; pageIndex++) {
          const pagedSearchBody = {
            ...defaultSearchBody,
            offset: defaultSearchBody.limit * pageIndex,
          };

          const searchReq = {
            ...geoContext,
            widget: {
              items: [
                {
                  rfk_id: getSearchWidget(),
                  entity: 'provider',
                  sources: [getProvidersSearchSrc()],
                  search: pagedSearchBody,
                },
              ],
            },
          };
          const response = await Axios.post('/api/discovery', searchReq);

          const nextProviders = Array.isArray(response.data.widgets[0].content)
            ? response.data.widgets[0].content
            : [];

          providers.push(...nextProviders);
        }

        setProviders(providers);
        setTotalProvidersCount(providers.length);

        providers.length >
        breakpointResultsCount[size as keyof BreakpointsCountType]
          ? setProvidersShowingCount(
              breakpointResultsCount[size as keyof BreakpointsCountType]
            )
          : setProvidersShowingCount(providers.length);
      } else {
        setNoResults(true);
        showSelected();
      }
    } catch (error) {
      console.log(`error:${error}`);
    } finally {
      setSearching(false);
    }
  };

  const loadMore = () => {
    setProvidersShowingCount(prevCount => {
      const isMobile = size === 'xs' || size === 'sm';

      const itemsToAdd = isMobile
        ? 3
        : breakpointRowsCount[size as keyof BreakpointsCountType];

      return Math.min(prevCount + itemsToAdd, totalProvidersCount);
    });
  };

  const showSelected = () => {
    setProviders(props.fields.Providers);
    setTotalProvidersCount(props.fields?.Providers?.length);

    props.fields.Providers?.length >
    breakpointResultsCount[size as keyof BreakpointsCountType]
      ? setProvidersShowingCount(
          breakpointResultsCount[size as keyof BreakpointsCountType]
        )
      : setProvidersShowingCount(props.fields.Providers.length);
  };

  const useCurrentLoc = () => {
    geolocate();

    const outerModal = document.querySelector('.pdc-modal') as HTMLElement;
    outerModal && outerModal.click();
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmedInput = inputLocation.trim();

    if (!trimmedInput || trimmedInput.length <= 1) {
      setSuggestions([]);
      suggestionRef.current = null;
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchAzureSuggestions(trimmedInput);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputLocation]);

  useEffect(() => {
    setProvidersShowing(providers.slice(0, providersShowingCount));
  }, [providers, providersShowingCount]);

  useEffect(() => {
    size &&
    totalProvidersCount >
      breakpointResultsCount[size as keyof BreakpointsCountType]
      ? setProvidersShowingCount(
          breakpointResultsCount[size as keyof BreakpointsCountType]
        )
      : setProvidersShowingCount(totalProvidersCount);
  }, [size]);

  useEffect(() => {
    coords && search();
  }, [coords]);

  useEffect(() => {
    const resize = () => {
      const viewportSize = getOsfViewportSize();
      setSize(viewportSize);
    };

    resize();
    window.addEventListener('resize', resize);

    if (
      props.fields?.Keywords?.value ||
      props.fields?.Specialties?.length > 0 ||
      props.fields?.ZipCode?.value
    ) {
      setIsDynamic(true);
    } else {
      showSelected();
    }

    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    !props.fields.ZipCode?.value && isDynamic && geolocate();

    if (props.fields.ZipCode?.value) {
      const setZipCoords = async () => {
        const subscriptionKey = getAzureMapsKey();

        if (!subscriptionKey) return;

        try {
          const response = await Axios.get(
            `${AZURE_MAPS_ENDPOINT}/search/address/json`,
            {
              params: {
                'api-version': '1.0',
                query: props.fields.ZipCode.value,
                countrySet: 'US',
                limit: 1,
                'subscription-key': subscriptionKey,
              },
              headers: {
                'Accept-Language': 'en-US',
              },
            }
          );

          const result = response.data?.results?.[0];

          if (!result?.position?.lat || !result?.position?.lon) return;

          const coordinates: LatLng = {
            lat: result.position.lat,
            lng: result.position.lon,
          };

          const address = {
            city: result?.address?.municipality,
            state: result?.address?.countrySubdivision,
            zip: result?.address?.postalCode || props.fields.ZipCode.value,
          };

          setCoords(coordinates);
          setAddressState(address, false);
        } catch (error) {
          console.log(`Azure geolocation error:${error}`);
        }
      };

      setZipCoords();
    }
  }, [isDynamic]);

  return (
    <div
      className={classNames('overhauled dynamic-provider-content', marginSize)}
      key={props.rendering.uid}
      id={`dynamic-provider-content-${props.rendering?.uid}`}
    >
      <div className="jss-inner-container">
        <div className="header-wrapper">
          {props.fields.Title?.value && (
            <h2 className="heading">{props.fields.Title.value}</h2>
          )}

          {props.fields.Description?.value && (
            <p className="description">{props.fields.Description.value}</p>
          )}

          <div className={noResults ? 'top-row no-results' : 'top-row'}>
            {isDynamic && (
              <div className="current-location">
                <div className="current-location-msg">
                  <div className="icon icon-location">
                    <Icons id="icon-pdc-current-location" />
                  </div>

                  <div className="osf-body-copy">
                    Current Location: {location}{' '}
                    <span className="change-btn">
                      <span className="parenthesis"> (</span>

                      <Modal
                        customClass="modal pdc-modal"
                        openBtnLabel="Change"
                        portalId="pdc-modal-root"
                        shouldCloseOnOverlayClick={true}
                      >
                        <div className="modal-details">
                          <h3>Set Your Location</h3>

                          <p>
                            Providing your location helps us connect you with
                            nearby care that’s convenient for you.
                          </p>

                          <div className="location-filter">
                            <label>Location (City or Zip)</label>

                            <div className="location-distance-panel">
                              <div className="location-distance-panel">
                                <div className="react-autosuggest__container input-icon-container">
                                  <div className="input-wrapper">
                                    <input
                                      placeholder="Enter Location"
                                      className="location-search-input"
                                      disabled={false}
                                      type="text"
                                      value={inputLocation || ''}
                                      onChange={event =>
                                        handleChange(event.target.value)
                                      }
                                      onKeyDown={event => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          handleSelect();
                                        }
                                      }}
                                    />

                                    <button
                                      className="btn-primary no-icon"
                                      onClick={() => handleSelect()}
                                    >
                                      Set
                                    </button>
                                  </div>

                                  {inputLocation?.length > 1 &&
                                    suggestions.length > 0 && (
                                      <div className="react-autosuggest__suggestions-container">
                                        {suggestions.map(suggestion => (
                                          <div
                                            className="typeahead-suggestion"
                                            key={suggestion.id}
                                            onMouseDown={event => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              handleSelect(suggestion);
                                            }}
                                          >
                                            <span className="suggestion-name">
                                              {suggestion.label}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                  {inputLocation?.length > 1 &&
                                    !isLoadingSuggestions &&
                                    suggestions.length === 0 && (
                                      <div className="react-autosuggest__suggestions-container">
                                        <div className="typeahead-suggestion">
                                          <span className="suggestion-name">
                                            No locations found
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>

                            <div className="current-location">
                              <div className="icon icon-distance">
                                <Icons id="icon-distance" />
                              </div>

                              <button onClick={() => useCurrentLoc()}>
                                Use my current location
                              </button>
                            </div>
                          </div>
                        </div>
                      </Modal>

                      <span className="parenthesis">)</span>
                    </span>
                  </div>
                </div>

                <div id="pdc-modal-root"></div>

                {noResults && (
                  <p className="no-providers-msg">
                    No providers within{' '}
                    {props.fields.Radius?.fields?.heading?.value ||
                      Math.round(convertToMI(DEFAULT_DISTANCE))}{' '}
                    miles
                  </p>
                )}
              </div>
            )}

            {!noResults && !searching && (
              <p className="showing-msg">
                Showing {providersShowingCount} of {totalProvidersCount}{' '}
                providers
              </p>
            )}

            {noResults && isDynamic && (
              <div className="showing-all">
                <Icons id="icon-info-thin"></Icons>
                <p>Showing All Featured Providers</p>
              </div>
            )}
          </div>
        </div>

        <div className="wrapper">
          {(geolocating || searching) && <h1>LOADING</h1>}

          {isDynamic &&
            !noResults &&
            !searching &&
            providers?.length > 0 &&
            providersShowing?.map((p: ProviderFields) => {
              return (
                <ProviderCard
                  provider={p}
                  key={`provider-${getProviderIdentityKey(p)}`}
                />
              );
            })}

          {(noResults || !isDynamic) &&
            !searching &&
            providers?.length > 0 &&
            providersShowing?.map((p: ProviderItem) => {
              return (
                <ManualProviderCard
                  provider={p}
                  key={`manual-provider-${getProviderIdentityKey(p)}`}
                />
              );
            })}
        </div>

        {providersShowingCount < totalProvidersCount && (
          <button className="btn-tertiary load-btn" onClick={() => loadMore()}>
            Load more
          </button>
        )}
      </div>
    </div>
  );
};

export default DynamicProviderContent;
